import React, { useState } from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import { SafeAreaView } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { useTheme } from "../context/ThemeContext";
import { getThemeColors } from "../utils/themeStyles";
import { usePlayer } from "../context/PlayerContext";
import { ALL_CHARACTERS } from "../data/storyCharacters";

interface AccountItem {
  key: string;
  title: string;
  subtitle: string;
  iconName: string;
  iconBg: string;
  route?: string; // écran de destination (sinon action locale)
}

const ACCOUNT_ITEMS: AccountItem[] = [
  {
    key: "my-information",
    title: "My Information",
    subtitle: "Personal details and preferences",
    iconName: "person",
    iconBg: "#3B82F6",
    route: "MyInformation",
  },
  {
    key: "dashboard",
    title: "Dashboard",
    subtitle: "View your progress and statistics",
    iconName: "bar-chart",
    iconBg: "#1D4ED8",
    route: "Dashboard",
  },
  {
    key: "leaderboard",
    title: "Leaderboard",
    subtitle: "Rankings and competitions",
    iconName: "trophy",
    iconBg: "#F59E0B",
    route: "Leaderboard",
  },
  {
    key: "payment-methods",
    title: "Payment Methods",
    subtitle: "Manage billing and cards",
    iconName: "card",
    iconBg: "#10B981",
    route: "PaymentMethods",
  },
  {
    key: "invoices",
    title: "Invoices",
    subtitle: "Purchase history and receipts",
    iconName: "document-text",
    iconBg: "#EF4444",
    route: "Invoices",
  },
];

// Pick 10 random avatars from all characters (stable selection)
const AVATAR_POOL = ALL_CHARACTERS.slice(0, 10);

export default function ProfileScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme, toggleTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { playerName, playerAvatar, setPlayerName, setPlayerAvatar } = usePlayer();

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(playerName);

  const handleItemPress = (item: AccountItem) => {
    if (item.route) {
      navigation.navigate(item.route as never);
    } else {
      // "My Information" : pas d'écran dédié -> édition du nom de joueur ici.
      setNameInput(playerName);
      setEditingName(true);
    }
  };

  const handleSaveName = () => {
    if (nameInput.trim()) {
      setPlayerName(nameInput.trim());
    }
    setEditingName(false);
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Page title */}
        <Text style={[styles.pageTitle, { color: colors.textPrimary }]}>Account</Text>

        {/* Player identity card */}
        <View style={[styles.identityCard, { backgroundColor: colors.cardBackground, shadowColor: colors.cardShadow }]}>
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>Mon identite de joueur</Text>

          {/* Current avatar + name */}
          <View style={styles.identityRow}>
            <View style={styles.avatarWrapper}>
              {playerAvatar ? (
                <Image source={playerAvatar} style={styles.currentAvatar} />
              ) : (
                <View style={[styles.currentAvatarPlaceholder, { backgroundColor: colors.surfaceSecondary }]}>
                  <Ionicons name="person" size={28} color={colors.textTertiary} />
                </View>
              )}
            </View>
            <View style={{ flex: 1 }}>
              {editingName ? (
                <View style={styles.nameEditRow}>
                  <TextInput
                    style={[styles.nameInput, { backgroundColor: colors.inputBackground, borderColor: colors.inputBorder, color: colors.inputText }]}
                    value={nameInput}
                    onChangeText={setNameInput}
                    onSubmitEditing={handleSaveName}
                    autoFocus
                    returnKeyType="done"
                    maxLength={24}
                    placeholder="Votre nom de joueur"
                    placeholderTextColor={colors.inputPlaceholder}
                  />
                  <TouchableOpacity style={[styles.saveBtn, { backgroundColor: colors.primary }]} onPress={handleSaveName}>
                    <Text style={styles.saveBtnText}>OK</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity style={styles.nameDisplayRow} onPress={() => { setNameInput(playerName); setEditingName(true); }}>
                  <Text style={[styles.playerNameText, { color: colors.textPrimary }]}>{playerName}</Text>
                  <Ionicons name="pencil" size={16} color={colors.textTertiary} style={{ marginLeft: 8 }} />
                </TouchableOpacity>
              )}
              <Text style={[styles.nameLabelHint, { color: colors.textTertiary }]}>Nom de joueur</Text>
            </View>
          </View>

          {/* Avatar grid */}
          <Text style={[styles.avatarGridLabel, { color: colors.textSecondary }]}>Choisir votre avatar</Text>
          <View style={styles.avatarGrid}>
            {AVATAR_POOL.map((char, idx) => {
              const isSelected = playerAvatar === char.avatar;
              return (
                <TouchableOpacity
                  key={`${char.name}-${idx}`}
                  onPress={() => setPlayerAvatar(char.avatar)}
                  style={[
                    styles.avatarGridItem,
                    isSelected && { borderColor: colors.primary, borderWidth: 2.5 },
                  ]}
                >
                  <Image source={char.avatar} style={styles.avatarGridImage} />
                  {isSelected && (
                    <View style={[styles.avatarCheckBadge, { backgroundColor: colors.primary }]}>
                      <Ionicons name="checkmark" size={10} color="#FFF" />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Main items */}
        <View style={styles.section}>
          {ACCOUNT_ITEMS.map((item) => (
            <TouchableOpacity
              key={item.key}
              activeOpacity={0.75}
              onPress={() => handleItemPress(item)}
              style={[
                styles.card,
                {
                  backgroundColor: colors.cardBackground,
                  shadowColor: colors.cardShadow,
                },
              ]}
            >
              <View style={[styles.iconCircle, { backgroundColor: item.iconBg }]}>
                <Ionicons name={item.iconName as any} size={22} color="#FFFFFF" />
              </View>
              <View style={styles.cardText}>
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{item.title}</Text>
                <Text style={[styles.cardSubtitle, { color: colors.textSecondary }]}>{item.subtitle}</Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />
            </TouchableOpacity>
          ))}
        </View>

        {/* Settings section */}
        <Text style={[styles.sectionTitleUpper, { color: colors.textSecondary }]}>Settings</Text>
        <View style={styles.section}>
          <View
            style={[
              styles.card,
              {
                backgroundColor: colors.cardBackground,
                shadowColor: colors.cardShadow,
              },
            ]}
          >
            <View style={[styles.iconCircle, { backgroundColor: "#6366F1" }]}>
              <Ionicons name="moon" size={22} color="#FFFFFF" />
            </View>
            <View style={styles.cardText}>
              <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>Dark Theme</Text>
              <Text style={[styles.cardSubtitle, { color: colors.textSecondary }]}>
                Toggle between light and dark mode
              </Text>
            </View>
            <Switch
              value={isDarkTheme}
              onValueChange={toggleTheme}
              trackColor={{ false: "#CBD5E1", true: "#3B82F6" }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { padding: 20, paddingBottom: 40 },
  pageTitle: { fontSize: 28, fontWeight: "bold", marginBottom: 20 },
  identityCard: {
    borderRadius: 14,
    padding: 16,
    marginBottom: 24,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 14,
  },
  sectionTitleUpper: {
    fontSize: 13,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 10,
  },
  identityRow: { flexDirection: "row", alignItems: "center", marginBottom: 20, gap: 14 },
  avatarWrapper: {},
  currentAvatar: { width: 64, height: 64, borderRadius: 32 },
  currentAvatarPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  nameEditRow: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 },
  nameInput: {
    flex: 1,
    height: 38,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 10,
    fontSize: 15,
  },
  saveBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  saveBtnText: { color: "#FFF", fontWeight: "700", fontSize: 14 },
  nameDisplayRow: { flexDirection: "row", alignItems: "center", marginBottom: 2 },
  playerNameText: { fontSize: 18, fontWeight: "700" },
  nameLabelHint: { fontSize: 12 },
  avatarGridLabel: { fontSize: 13, fontWeight: "600", marginBottom: 10 },
  avatarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  avatarGridItem: {
    width: 60,
    height: 60,
    borderRadius: 30,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: "transparent",
    position: "relative",
  },
  avatarGridImage: { width: "100%" as any, height: "100%" as any },
  avatarCheckBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  section: { gap: 12, marginBottom: 24 },
  card: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    padding: 14,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14,
    flexShrink: 0,
  },
  cardText: { flex: 1 },
  cardTitle: { fontSize: 15, fontWeight: "700", marginBottom: 2 },
  cardSubtitle: { fontSize: 13 },
});
